/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useCartContext } from "@farmlands/commerce";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { usePermission } from "../../../hooks/usePermission";
import { ATOMIC_STREAM_INBOX_ID, useAtomic } from "../../../services/atomic/AtomicProvider";
import { selectConfigByKey } from "../../../services/firebase/remote-config/remoteConfig.slice";
import { useFeatureFlag } from "../../../services/firebase/remote-config/useFeatureFlag";
import { selectCanUseQuickOrder } from "../../azure-b2c/storage";
import { usePickUpStoreModal } from "../components/PickUpStoreModal";
import { selectCurrentCart } from "../storage/cart.slice";
import { CartDeliveryMode } from "../storage/cart.types";

export type CartHeaderData = {
  canUse: boolean | undefined;
  canCreateCartOrder: boolean;
  canViewProShop: boolean;
  cartItemCount: number;
  notificationCount: number;
  showNotifications: boolean;
  isDelivery: boolean;
  pickupOrDeliveryMode: "Pickup" | "Delivery" | null;
  pickupOrDeliveryAddress?: string;
  goToCart: () => void;
  openNotifications: () => void;
  openPickupModal: () => void;
};

export const useCartHeaderData = (): CartHeaderData => {
  const navigate = useNavigate();
  const currentCart = useSelector(selectCurrentCart);
  const canUse = useSelector(selectCanUseQuickOrder);
  const canCreateCartOrder = usePermission("FarmlandsProShop_CartOrderCreate").allowed;
  const canViewProShop = usePermission("FarmlandsProShop_ShopView").allowed;
  const atomic = useAtomic();
  const atomicEnabled = useSelector(selectConfigByKey("atomicEnabled"));
  const pickupModal = usePickUpStoreModal();
  const enableShopCartPage = useFeatureFlag("enableShopCartPage");
  const { items: cartContextItems } = useCartContext();

  const deliveryMode = currentCart?.cartDeliveryMode;
  const isDelivery = deliveryMode === CartDeliveryMode.DELIVERY;

  let pickupOrDeliveryMode: "Pickup" | "Delivery" | null = null;
  let pickupOrDeliveryAddress: string | undefined;

  if (deliveryMode === CartDeliveryMode.PICKUP && currentCart?.pickupStore?.orgUnitName) {
    pickupOrDeliveryMode = "Pickup";
    pickupOrDeliveryAddress = currentCart.pickupStore.orgUnitName;
  } else if (isDelivery && currentCart?.deliveryDetails?.address) {
    pickupOrDeliveryMode = "Delivery";
    pickupOrDeliveryAddress = currentCart.deliveryDetails.address.replace(/\n/g, ", ");
  }

  const cartItemCount: number = enableShopCartPage
    ? cartContextItems.length
    : (currentCart?.items.length ?? 0);

  return {
    canUse,
    canCreateCartOrder,
    canViewProShop,
    cartItemCount,
    notificationCount: atomic.messageCount,
    showNotifications: Boolean(atomicEnabled),
    isDelivery,
    pickupOrDeliveryMode,
    pickupOrDeliveryAddress,
    goToCart: () => navigate("/products/cart"),
    openNotifications: () => atomic.setDrawerOpen(ATOMIC_STREAM_INBOX_ID),
    openPickupModal: () => pickupModal.setOpen(true),
  };
};
